/**
 * A learner's public page, at `/u/<tag>`.
 *
 * Until this existed, one learner's record could be read in exactly one place:
 * a card inside the chat panel, by a reader who was signed in and in a room
 * with them (components/chat/ProfileCard.tsx). That is a reasonable card and
 * an unreasonable ceiling. A record nobody outside the product can open is not
 * something a learner has; it is something the product holds about them.
 *
 * ## Who this is for, which decides everything below
 *
 * Not a learner admiring their own progress — that is `?profile=me`, and it
 * has the stars and the streaks and the chapter table. This page is the thing
 * a learner hands to somebody who is deciding about them: a hiring manager
 * following a link off a CV or a profile, who has thirty seconds, has never
 * heard of this product, and has been trained by a decade of completion
 * certificates to ignore exactly this kind of page.
 *
 * That reader is the reason the page is ordered the way it is. The claim is
 * first and it is counted in **worlds**, because a world is a subject a
 * stranger can hold — "Recovery", "Team Git" — and a level number is an
 * internal address. Then the badges. Then the three properties that separate
 * this from a certificate of attendance: the work started from a repository
 * generated for that learner alone, the server graded the repository they left
 * behind rather than the commands they typed, and a checkpoint carries no
 * hints (docs/checkpoints.md). Then, last, where it all stops.
 *
 * XP is not the headline and deliberately no longer sits beside the name. It
 * is this product's internal currency, it means nothing to an outside reader,
 * and a game's score sitting where the claim goes is the single fastest way to
 * have the whole page filed as a game. It stays on each row, where it is a
 * detail of a confirmed completion rather than a summary of a person.
 *
 * ## What is on it, and why so little
 *
 * Confirmed checkpoints, and nothing else that counts.
 *
 * Everything the tab knows about a learner's practice — levels cleared, stars,
 * hints, attempts, chapters — was graded in the browser and pushed to
 * `/api/progress` as a backup of that browser's claims (docs/verification.md,
 * "Trust boundary"). On the learner's own screen that is fine; it is their
 * record of their own work, and the screen says who reported it. On a page
 * whose entire purpose is to be shown to somebody with no reason to trust the
 * person showing it, an unchecked number placed beside a checked one does not
 * inherit its caution — it borrows its credibility. So the practice record is
 * not here at all, and `?profile=me` remains the place it lives.
 *
 * What is here was written by a server that graded submitted bytes in an
 * isolated container and awarded a fixed amount of XP the browser never saw
 * (worker/verification.ts). That is the one part of this product's record that
 * a stranger has any reason to believe, and this page exists because that part
 * now exists.
 *
 * The page says out loud how much of the course can be checked at all, and it
 * counts that ceiling off the badge table rather than out of a sentence
 * somebody has to remember to edit. Printing the limit is the difference
 * between a record and a brochure: a reader who works out for themselves that
 * the badges cover a part of the course stops believing the part too.
 *
 * ## Why it is its own entry rather than a screen inside App
 *
 * App boots a virtual machine. The boot effect runs on mount for every visit,
 * downloads the guest from R2 and holds the boot screen until the level
 * catalogue has arrived (App.tsx, "Booting the VM and fetching the level
 * list"). For a stranger who clicked a link in a message, that is a hundred
 * megabytes and several seconds to look at a handful of badges — and it is
 * bandwidth billed to the product for a reader who has not asked for a course
 * yet.
 *
 * So main.tsx renders this instead of App when the path names a person. It
 * needs no VM, no missions, no account, no socket and no progress: one request
 * to one public route, and a page. That also makes it the only screen here
 * that works when the Worker is the only thing deployed.
 */
import { useEffect, useState } from 'react';
import { VERIFICATION_BADGES } from '@engineer-os/engine';
import type { PublicProfileProps } from '../contracts';
import { fetchPerson, type PublicPerson } from '../../lib/person';
import { certificatePath } from '../../lib/publicCertificate';
import { MEASURED_AGAINST_A_MODEL } from '../../lib/checkpoint';
import { ratioText } from '../../lib/efficiency';
import { calendarDate, shortDate, utcDate } from '../../lib/format';
import { useLocale, useStrings } from '../../state/useLocale';
import { Brand, LinkButton, Machine, Tag } from '../../ui';
import { Corner } from '../Corner';
import { OperatorPlate } from '../avatar/Operator';

/**
 * How far independent checking reaches, counted rather than written down.
 *
 * The page tells a stranger where its own claims stop, and a coverage figure
 * that lives in a sentence is a figure that goes stale the first time a world
 * gets a checkpoint and nobody thinks to edit the prose. Both numbers come off
 * the same table the badges are derived from, so the ceiling cannot disagree
 * with the shelf above it.
 */
const CHECKPOINT_WORLDS = VERIFICATION_BADGES.length;
const CHECKPOINT_LEVELS = VERIFICATION_BADGES.reduce((total, badge) => total + badge.worldLevels, 0);

/**
 * A world's name in the reader's language, or null for one this bundle has no
 * word for.
 *
 * The catalogue is `strings/home.ts`, unchanged and shared: a world's name is
 * already translated once there for the map, keyed on the English string the
 * missions themselves carry (docs/i18n.md §6.1). Translating it a second time
 * here would be two names for one world, drifting apart at the speed of
 * whichever one somebody remembered to edit.
 *
 * Null draws nothing, for the reason a mission with no title draws nothing: a
 * badge is a word, and a badge with a raw key on it is worse than one that is
 * not there.
 */
function worldName(s: ReturnType<typeof useStrings>, world: string): string | null {
  const worlds = s.home.worlds as Record<string, { name: string } | undefined>;
  return worlds[world]?.name ?? null;
}

/**
 * The screen, and the one request behind it.
 *
 * Split from the page below it because a page that fetches cannot be rendered
 * to markup without a network: `renderToStaticMarkup` does not run effects, so
 * a screenshot of this component would be a screenshot of the word "Loading".
 * `PersonPage` takes a record in hand and can be rendered straight to markup,
 * in both themes and both languages (docs/theme.md, docs/i18n.md).
 */
export function PublicProfile({ tag }: PublicProfileProps) {
  const s = useStrings();
  /** Absent while asking, null once it is known there is nobody here. */
  const [person, setPerson] = useState<PublicPerson | null | undefined>(undefined);

  useEffect(() => {
    let live = true;
    void fetchPerson(tag).then((p) => {
      if (live) setPerson(p);
    });
    return () => {
      live = false;
    };
  }, [tag]);

  // The tab's title carries the claim as well as the name: a bookmark, a
  // history entry and a second tab all read it. A link pasted into a chat or a
  // feed reads the card the Worker writes into this document before it is
  // served (worker/profile-card.ts), whose title is this same sentence.
  useEffect(() => {
    if (person) document.title = s.person.tab(person.name);
  }, [person, s]);

  return <PersonPage person={person} />;
}

/**
 * The page itself: a record, an empty record, or nobody.
 *
 * `undefined` is the request still being out, `null` is a link that does not
 * open onto anybody, and a record with no completions in it is the ordinary
 * case rather than an edge — everybody has one of those before they have
 * anything else.
 */
export function PersonPage({ person }: { person: PublicPerson | null | undefined }) {
  const s = useStrings();
  const { locale } = useLocale();

  return (
    <div className="profile person">
      {/* The corner every page has, chat door included (components/Corner.tsx). */}
      <Corner />
      <header className="profile-top">
        {/* Home is `/` here and `?` everywhere else. This page is the only
            screen that does not live in the query string, so emptying the
            query string would leave a reader who clicked the mark exactly
            where they were. */}
        <Brand compact href="/" />
      </header>

      <main className="profile-page">
        {person === undefined ? (
          <p className="note" role="status">
            {s.person.loading}
          </p>
        ) : person === null ? (
          /* One answer for a mistyped link, an account that was closed and a
             Worker that is not there — worker/profile.ts says why they are not
             told apart. */
          <section className="person-missing">
            <h1 className="profile-name">{s.person.missing.title}</h1>
            <p className="note">{s.person.missing.body}</p>
            <p className="person-cta">
              <LinkButton variant="solid" href="/">
                {s.person.cta}
              </LinkButton>
            </p>
          </section>
        ) : (
          <>
            {(() => {
              // Derived once, here, because three sections below need the same
              // answer and a page that counted its badges twice could print
              // two different numbers on one screen.
              const badges = person.badges
                .map((badge) => ({ badge, name: worldName(s, badge.world) }))
                .filter((b): b is { badge: typeof b.badge; name: string } => b.name !== null);
              const confirmed = person.completions.length > 0;

              return (
                <>
                  <section className="profile-who">
                    {/* A figure and no fallback picture. An account's Google
                        photo is worn inside the product
                        (components/profile/Profile.tsx) and is not republished
                        to strangers from here: the operator is the identity
                        this product issues, and a seed that is absent draws
                        nothing rather than reaching for somebody's face. */}
                    {person.avatarSeed && <OperatorPlate seed={person.avatarSeed} size={128} />}
                    <div className="profile-who-copy">
                      <p className="label">{s.person.label}</p>
                      {/* A name is the person's own and may be in either
                          script, so it takes its direction from itself
                          (docs/i18n.md §7). */}
                      <h1 className="profile-name" dir="auto">
                        {person.name}
                      </h1>
                      <p className="profile-marks">
                        {/* The claim, in the unit a stranger can hold. It is
                            the only number at the top of this page. */}
                        {badges.length > 0 && <Tag tone="on">{s.person.worlds(badges.length)}</Tag>}
                        <span className="note">
                          {s.person.memberSince(shortDate(new Date(person.joined), locale))}
                        </span>
                      </p>
                      {/* One sentence naming the mechanism, before any badge.
                          A reader who has not been told that a server did the
                          grading reads everything below as a course scoring
                          itself. Inside the header block rather than under it:
                          the page's sections are spaced for sections, and this
                          belongs to the name it qualifies. */}
                      {confirmed && <p className="person-summary">{s.person.summary}</p>}
                    </div>
                  </section>

                  {/* The certificates, above the worlds: each is the larger
                      claim, a fixed set of checkpoints passed and frozen at
                      issue. Only valid ones reach this page (worker/profile.ts),
                      and each is a link rather than a description, because
                      what a certificate claims is said on its own page, where
                      a reader who wants to believe it goes to check. */}
                  {person.certificates.length > 0 && (
                    <section className="person-proof" aria-label={s.person.certificates.label}>
                      <h2 className="profile-h2">{s.person.certificates.label}</h2>
                      <ul className="person-certificates">
                        {person.certificates.map((c) => (
                          <li className="person-certificate" key={c.id}>
                            <a href={certificatePath(c.id)}>
                              <span className="person-world-mark" aria-hidden="true" />
                              <span className="person-world-copy">
                                <strong className="person-certificate-name">{s.certificate.names[c.certificate]}</strong>
                                <span className="note">
                                  {s.person.certificates.issued(calendarDate(new Date(c.issuedAt), locale))} ·{' '}
                                  <span className="person-certificate-go">{s.person.certificates.check}</span>
                                </span>
                              </span>
                            </a>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  {badges.length > 0 && (
                    <section className="person-proof" aria-label={s.person.confirmed.badges.label}>
                      <h2 className="profile-h2">{s.person.confirmed.badges.label}</h2>
                      {/* The worlds, above the checkpoints that earned them. A
                          reader who stops here should have been told something
                          true and nothing more, which is why the coverage sits
                          on each badge and the caveat sits directly under the
                          row rather than at the foot of the page with the
                          other small print. */}
                      <ul className="person-world-list">
                        {badges.map(({ badge, name }) => (
                          <li className="person-world" key={badge.world}>
                            <span className="person-world-mark" aria-hidden="true" />
                            <span className="person-world-copy">
                              {/* No `dir="auto"`: a world's name is catalogue
                                  copy in the page's language, and an Arabic
                                  one that opens on a Latin word (`Git في
                                  الفريق`) would be set left to right. */}
                              <strong className="person-world-name">{name}</strong>
                              <span className="note">
                                {s.person.confirmed.badges.coverage(badge.missions.length, badge.worldLevels)}
                              </span>
                            </span>
                          </li>
                        ))}
                      </ul>
                      {/* One sentence for the row, and the careful one wins: a
                          reader cannot be expected to work out which badge in
                          a list was the weaker claim, so while any drawn badge
                          rests on a shared starting repository the whole row
                          says so. */}
                      <p className="note person-world-caveat">
                        {badges.every(({ badge }) => badge.salted)
                          ? s.person.confirmed.badges.learner
                          : s.person.confirmed.badges.artifact}
                      </p>

                      {/* The argument, as three terms rather than a paragraph.
                          This is the part that distinguishes the page from a
                          certificate of completion, and a reader who skims
                          three bold words has still received it. */}
                      <h3 className="label person-how-label">{s.person.how.label}</h3>
                      {/* A description list, because that is what this is: a
                          term and what it means. The label is a heading beside
                          it rather than a row inside it — a `dl` takes `dt`,
                          `dd` and `div` and nothing else, and a paragraph in
                          there is markup that renders and does not parse. */}
                      <dl className="person-how">
                        {[s.person.how.own, s.person.how.state, s.person.how.unaided].map((item) => (
                          <div className="person-how-item" key={item.term}>
                            <dt className="person-how-term">{item.term}</dt>
                            <dd className="person-how-body note">{item.body}</dd>
                          </div>
                        ))}
                      </dl>
                    </section>
                  )}

                  <section className="profile-record" aria-label={s.person.confirmed.label}>
                    <h2 className="profile-h2">{s.person.confirmed.label}</h2>
                    <p className="note">{s.person.confirmed.what}</p>

                    {!confirmed ? (
                      <div className="person-empty">
                        <p className="person-empty-line">{s.person.confirmed.none}</p>
                        <p className="note">{s.person.confirmed.noneWhy}</p>
                      </div>
                    ) : (
                      <ul className="person-badges">
                        {person.completions.map((c) => {
                          // A checkpoint with no word for it in this build is
                          // not drawn. The Worker already refuses to send one
                          // the contract has dropped; this is the other
                          // direction — a bundle older than the Worker — and a
                          // badge with a raw id on it would be worse than a
                          // badge that is not there.
                          //
                          // There is no level number beside these. There used
                          // to be, parsed off an `l13-` style id, and it has
                          // gone with the levels it belonged to: a checkpoint
                          // is `cp-recovery`, it stands over a world rather
                          // than at a position in a list, and its own title
                          // already says which world.
                          const title =
                            s.verification.missions[c.missionId as keyof typeof s.verification.missions];
                          if (!title) return null;
                          const measured = MEASURED_AGAINST_A_MODEL.get(c.missionId);
                          return (
                            <li className="person-badge" key={c.missionId}>
                              <span className="person-badge-mark" aria-hidden="true" />
                              <span className="person-badge-copy">
                                <strong className="person-badge-title" dir="auto">
                                  {title}
                                </strong>
                                <span className="note">
                                  {s.person.confirmed.on(shortDate(new Date(c.verifiedAt), locale))} ·{' '}
                                  {s.person.confirmed.xp(c.xp)}
                                </span>
                                {measured && (
                                  <span className="note">
                                    {s.person.confirmed.measured(
                                      measured.model,
                                      measured.modelId,
                                      utcDate(new Date(measured.on), locale),
                                      measured.runs,
                                    )}
                                  </span>
                                )}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    )}

                    {/* What their agents cost, from three metered checkpoint
                        passes up (docs/efficiency-plan.md, decision 4). One
                        line and what it means, under the passes it is a
                        median of: the median of every pass, never the best,
                        priced with a named price book, so a reader who
                        wants to know what "par" is has the answer beside it.
                        Nothing at all below three passes, which is `null`. */}
                    {person.efficiency && (
                      <div className="person-efficiency">
                        <h3 className="label">{s.efficiency.person.label}</h3>
                        <p className="person-efficiency-line">
                          {s.efficiency.person.line(
                            ratioText(person.efficiency.medianRatio, locale),
                            person.efficiency.passes,
                            person.efficiency.book,
                          )}
                        </p>
                        <p className="note">{s.efficiency.person.what}</p>
                      </div>
                    )}

                    {/* The ceiling, under the record rather than over it: it is
                        about the course, not about this learner, and a reader
                        should meet the record before they meet its limits. */}
                    <p className="note person-pilot">
                      {s.person.confirmed.ceiling(CHECKPOINT_WORLDS, CHECKPOINT_LEVELS)}
                    </p>
                    {/* Who is vouching, said plainly. A reader who is weighing
                        a credential is exactly the reader who will work out
                        that this is one deployment's own server, and being
                        told buys more than the word it declines to use. */}
                    <p className="note">{s.person.provenance}</p>
                    {/* The contract the server graded under. Machine text
                        because it is an identifier, and quiet because it is
                        for the one reader in a hundred who wants to know that
                        this is versioned. */}
                    {person.version !== '' && (
                      <p className="note">
                        <Machine>{person.version}</Machine>
                      </p>
                    )}
                  </section>
                </>
              );
            })()}

            <section className="person-cta-band">
              <p className="note">{s.person.ctaWhy}</p>
              <LinkButton variant="solid" href="/">
                {s.person.cta}
              </LinkButton>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

export default PublicProfile;
